import React, { useEffect, useMemo, useState } from "react";
import TherapistSideBar from "../../common/TherapistSidebar/TherapistSidebar";
import TherapistCalendarGrid from "./TherapistCalendarGrid";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Plus, Check, X, Clock, RefreshCw } from "lucide-react";
import ClientCard from "../TherapistHomePage/ClientCard";
import Calendar from "../../assets/images/icons/homePage/calendar.svg";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentTherapist } from "../../redux/therapistSlice";
import { getMyAvailabilities, deleteAvailability } from "../../redux/availabilitySlice";
import AvailabilityModal from "./AvailabilityModal";
import {
  formatDateRangeForZone,
  parseBackendDate,
  USER_TIMEZONE,
} from "../../utils/dateUtils";
import { getTherapistRequests } from "../../redux/requestSlice";

// Müsaitlik verilerini işleme fonksiyonu
const processAvailabilityData = (availabilities) => {
  if (!availabilities || !Array.isArray(availabilities) || availabilities.length === 0) {
    return [];
  }
  
  return availabilities.map(slot => {
    try {
      const startTime = new Date(slot.startDate);
      const endTime = new Date(slot.endDate);
      
      // Notes alanından fiyat bilgisini çıkar (eğer varsa)
      let price = 0;
      if (slot.notes) {
        const priceMatch = slot.notes.match(/Ücret: (\d+) TL/);
        if (priceMatch && priceMatch[1]) {
          price = parseInt(priceMatch[1]);
        }
      }
      
      // Backend'den gelen saatleri olduğu gibi kullan
      const startHour = startTime.getHours();
      const startMinute = startTime.getMinutes();
      const endHour = endTime.getHours();
      const endMinute = endTime.getMinutes();
      
      const startFormatted = `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
      const endFormatted = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
      
      return {
        date: startTime,
        day: startTime.getDate(),
        month: startTime.getMonth(),
        year: startTime.getFullYear(),
        startTime,
        endTime,
        price: price,
        formatted: {
          date: `${startTime.getDate()}-${startTime.getMonth()}-${startTime.getFullYear()}`,
          time: `${startFormatted} - ${endFormatted}`
        },
        id: slot._id || `temp-${Date.now()}`
      };
    } catch (error) {
      console.error("Müsaitlik veri işleme hatası:", error);
      return null;
    }
  }).filter(Boolean);
};

function TherapistTherapyPlanner() {
  const { t } = useTranslation();
  const today = new Date();
  const todayDate = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();
  const dispatch = useDispatch();

  const [selectedDate, setSelectedDate] = useState(todayDate);
  const [currentMonth, setCurrentMonth] = useState(todayMonth);
  const [currentYear, setCurrentYear] = useState(todayYear);
  const [appointments, setAppointments] = useState({});
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [selectedDateForAvailability, setSelectedDateForAvailability] = useState(null);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [dataFetched, setDataFetched] = useState(false);
  
  const therapistState = useSelector((state) => state.therapist) || {};
  const availabilityState = useSelector((state) => state.availability) || {};
  const requestState = useSelector((state) => state.requests) || {};

  const { therapist, loading } = therapistState;
  const { availabilities, loading: availabilityLoading, error: availabilityError } = availabilityState;
  
  // Terapist verilerini yükle
  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (token && !therapist) {
      dispatch(getCurrentTherapist());
    }
  }, [dispatch, therapist]);
  
  useEffect(() => {
    dispatch(getTherapistRequests());
  }, [dispatch]);
  
  const therapistRequests = useMemo(() => {
    const requestList = Array.isArray(requestState.requests) ? requestState.requests : [];
    if (Array.isArray(therapist?.requests) && therapist.requests.length > 0) {
      return therapist.requests;
    }
    return requestList;
  }, [therapist, requestState.requests]);

  // Terapist verisi yüklendiğinde müsaitlikleri çek
  useEffect(() => {
    if (therapist && therapist._id && !dataFetched) {
      // ID'yi string olarak gönderelim
      const therapistIdString = String(therapist._id);
      dispatch(getMyAvailabilities(therapistIdString))
        .catch(err => {
          console.error("Müsaitlik verileri alınırken hata oluştu:", err);
          // 404 hatası gelebilir (henüz müsaitlik yoksa)
          setDataFetched(true); // Yine de dataFetched'i true yapalım ki sürekli istek atmasın
        });
      setDataFetched(true);
    }
  }, [therapist, dispatch, dataFetched]);
  
  // Müsaitlik verilerini işle
  useEffect(() => {
    if (availabilities && Array.isArray(availabilities) && availabilities.length > 0) {
      const processedSlots = processAvailabilityData(availabilities);
      setAvailabilitySlots(processedSlots);
    }
  }, [availabilities]);
  
  // Randevuları işle
  useEffect(() => {
    if (therapistRequests && therapistRequests.length > 0) {
      const processedAppointments = {};

      const coerceToDate = (value) => {
        if (!value) return null;
        if (value instanceof Date) {
          return Number.isNaN(value.getTime()) ? null : value;
        }
        if (typeof value === "number") {
          const dateFromNumber = new Date(value);
          return Number.isNaN(dateFromNumber.getTime()) ? null : dateFromNumber;
        }

        if (typeof value === "string") {
          const trimmed = value.trim();
          if (!trimmed) {
            return null;
          }
          const parsed = parseBackendDate(trimmed);
          if (parsed) {
            return parsed;
          }

          const fallback = new Date(trimmed);
          return Number.isNaN(fallback.getTime()) ? null : fallback;
        }

        return null;
      };

      therapistRequests.forEach((request) => {
        try {
          const slotInfo =
            request.availabilityIds && request.availabilityIds.length > 0
              ? request.availabilityIds[0]
              : null;

          const slotDetails =
            slotInfo && typeof slotInfo === "object" ? slotInfo : null;

          const selectedSlot =
            request.selectedSlot && typeof request.selectedSlot === "object"
              ? request.selectedSlot
              : null;

          const meetingInfo =
            request.meetingId && typeof request.meetingId === "object"
              ? request.meetingId
              : null;

          const rawStartValue =
            slotDetails?.startDate ??
            slotDetails?.startTime ??
            slotDetails?.start ??
            selectedSlot?.startTime ??
            selectedSlot?.startDate ??
            selectedSlot?.start ??
            meetingInfo?.startDate ??
            meetingInfo?.startTime ??
            meetingInfo?.meetingStartDate ??
            request.sessionDate ??
            request.startDate ??
            request.startTime ??
            request.appointmentDate ??
            request.date;

          const rawEndValue =
            slotDetails?.endDate ??
            slotDetails?.endTime ??
            slotDetails?.end ??
            selectedSlot?.endTime ??
            selectedSlot?.endDate ??
            selectedSlot?.end ??
            meetingInfo?.endDate ??
            meetingInfo?.endTime ??
            meetingInfo?.meetingEndDate ??
            request.sessionEndDate ??
            request.endDate ??
            request.endTime;

          const startDate =
            coerceToDate(rawStartValue) || coerceToDate(request.createdAt);

          const endDate =
            coerceToDate(rawEndValue) ||
            (startDate ? new Date(startDate.getTime() + 60 * 60 * 1000) : null);

          if (!startDate) {
            return;
          }

          const day = startDate.getDate();
          const month = startDate.getMonth();
          const year = startDate.getFullYear();

          const formattedRange = formatDateRangeForZone(
            startDate,
            endDate,
            USER_TIMEZONE
          );

          const timeRangeFromFormatted = formattedRange
            ? formattedRange.split(" ").slice(1).join(" ")
            : "";

          const fallbackStart = `${startDate
            .getHours()
            .toString()
            .padStart(2, "0")}:${startDate
            .getMinutes()
            .toString()
            .padStart(2, "0")}`;

          const fallbackEnd = endDate
            ? `${endDate
                .getHours()
                .toString()
                .padStart(2, "0")}:${endDate
                .getMinutes()
                .toString()
                .padStart(2, "0")}`
            : "";

          const fallbackRange = fallbackEnd
            ? `${fallbackStart} - ${fallbackEnd}`
            : fallbackStart;

          const timeRange = timeRangeFromFormatted || fallbackRange;

          const key = `${day}-${month}-${year}`;

          if (!processedAppointments[key]) {
            processedAppointments[key] = [];
          }

          const resolvedUser = request.userDetails || request.userId || {};
          
          // Refund durumunu kontrol et
          const isRefunded = request.payment && 
            (request.payment.status === 'refunded' || 
             (request.payment.details?.refund?.status === 'succeeded'));

          const availabilityId =
            (slotInfo && typeof slotInfo === "object" ? slotInfo._id : slotInfo) ||
            null;

          processedAppointments[key].push({
            time: timeRange,
            userDetails: {
              name: resolvedUser?.name || "Unknown Client",
              surname: resolvedUser.surname || "",
            },
            status: request.status || "pending",
            description: request.description || "",
            requestId: request.requestId || request._id,
            availabilityId: availabilityId ? String(availabilityId) : null,
            startTime: startDate,
            endTime: endDate,
            price:
              selectedSlot?.price ||
              slotDetails?.price ||
              request.price ||
              0,
            isRefunded: isRefunded,
            payment: request.payment,
          });
        } catch (error) {
          console.error("Randevu tarih bilgisi işlenirken hata oluştu:", error);
        }
      });

      setAppointments(processedAppointments);
    } else {
      setAppointments({});
    }
  }, [therapistRequests]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  
  // Müsait saat eklemek için modal açma
  const handleAddAvailability = (day, month, year) => {
    setSelectedDateForAvailability({
      day,
      month,
      year,
      fullDate: new Date(year, month, day)
    });
    setAvailabilityModalOpen(true);
  };
  
  // Tarihe tıklandığında 
  const handleDateClick = (day, month, year) => {
    setSelectedDate(day);
  };
  
  // Randevu durumunu güncelle
  const handleUpdateAppointmentStatus = (requestId, newStatus) => {
    // Burada API ile entegre olup randevu durumu güncellenecek
    console.log(`Randevu ${requestId} durumu "${newStatus}" olarak güncelleniyor`);
    
    // UI'ı hemen güncelle
    const updatedAppointments = {};
    
    Object.keys(appointments).forEach(dateKey => {
      const updatedDayAppointments = appointments[dateKey].map(appointment => {
        if (appointment.requestId === requestId) {
          return { ...appointment, status: newStatus };
        }
        return appointment;
      });
      
      updatedAppointments[dateKey] = updatedDayAppointments;
    });
    
    setAppointments(updatedAppointments);
  };
  
  // Müsaitlik silme işlemi
  const handleDeleteAvailability = (slot) => {
    if (!slot.id) return;
    
    // Geçici UI güncellemesi (optimistic update)
    const updatedSlots = availabilitySlots.filter(s => s.id !== slot.id);
    setAvailabilitySlots(updatedSlots);
    
    // Geçici ID ise API çağrısı yapma
    if (slot.id.startsWith('temp-')) {
      return;
    }
    
    // API ile sil
    dispatch(deleteAvailability(slot.id))
      .then(resultAction => {
        if (!deleteAvailability.fulfilled.match(resultAction)) {
          console.error("Silme hatası:", resultAction.payload);
          // Başarısız olursa eski listeye dön
          setAvailabilitySlots(availabilitySlots);
        }
      })
      .catch(err => {
        console.error("Silme işlemi başarısız:", err);
        // Hata durumunda eski listeye dön
        setAvailabilitySlots(availabilitySlots);
      });
  };

  // Seçilen tarih için randevular
  const selectedDateAppointments = appointments[`${selectedDate}-${currentMonth}-${currentYear}`] || [];
  
  // Seçilen tarih için tüm slot'lar
  const selectedDateAvailabilities = availabilitySlots.filter(slot =>
    slot.day === selectedDate &&
    slot.month === currentMonth &&
    slot.year === currentYear
  );

  // Randevusu olan slot ID'leri
  const bookedAvailabilityIds = new Set(
    selectedDateAppointments
      .filter(apt => apt.availabilityId && !apt.isRefunded)
      .map(apt => apt.availabilityId)
  );

  // Gerçekten müsait olan slot'lar (randevusu olmayanlar)
  const freeSlots = selectedDateAvailabilities.filter(
    slot => !bookedAvailabilityIds.has(String(slot.id))
  );

  // Randevuya bağlı slot'lar (dolu gösterilecek)
  const bookedSlots = selectedDateAvailabilities.filter(
    slot => bookedAvailabilityIds.has(String(slot.id))
  );

  const normalizeStatus = (status) => (status || "").toLowerCase();
  const isPendingStatus = (status) => normalizeStatus(status) === "pending";
  const isConfirmedStatus = (status) => ["confirmed", "accepted", "completed"].includes(normalizeStatus(status));
  const isCompletedStatus = (status) => normalizeStatus(status) === "completed";

  const flattenedAppointments = useMemo(() => {
    const allAppointments = Object.values(appointments || {}).flat();
    const now = new Date();
    return allAppointments.filter(apt => {
      if (!apt.startTime) return false;
      const aptDate = new Date(apt.startTime);
      return aptDate > now;
    });
  }, [appointments]);

  const completedAppointments = useMemo(() => {
    const allAppointments = Object.values(appointments || {}).flat();
    return allAppointments.filter(apt => {
      if (!apt.startTime) return false;
      return isCompletedStatus(apt.status);
    });
  }, [appointments]);

  // Randevu durumu renkleri
  const getStatusColor = (status, isRefunded = false) => {
    // Önce refund durumunu kontrol et
    if (isRefunded) {
      return { bg: "bg-purple-100", text: "text-purple-800" };
    }
    
    const normalized = normalizeStatus(status);

    switch(normalized) {
      case 'pending':
        return { bg: "bgYellow", text: "text-yellow-800" };
      case 'confirmed':
      case 'accepted':
      case 'completed':
        return { bg: "bgGreen", text: "text-green-800" };
      case 'cancelled':
      case 'canceled':
        return { bg: "bgGray", text: "text-gray-800" };
      case 'rejected':
        return { bg: "bgRed", text: "text-red-800" };
      default:
        return { bg: "bgGray", text: "text-gray-800" };
    }
  };

  // Yükleniyor durumu
  if (loading) {
    return (
      <div className="flex flex-col md:flex-row min-h-screen">
        <TherapistSideBar />
        
        <div className="flex-1 p-4 md:p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-xl font-poppins text-primary">
              {t("loading")}...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <TherapistSideBar />

      <div className="flex-1 p-4 md:p-8">
        <div className="flex flex-col lg:flex-row gap-4 md:gap-5">
          {/* Randevu Detayları */}
          <div className="w-full md:w-[350px] space-y-2 bg-white rounded-2xl p-4 md:p-6 shadow-sm order-1 md:order-2">
            <h2 className="text-xl font-poppins">
              {selectedDate}{" "}
              {t(
                `${new Date(currentYear, currentMonth).toLocaleString(
                  "en-US",
                  { month: "long" }
                )}`
              )}
            </h2>

            <div className="space-y-4">
              {/* Randevular */}
              <div className="mb-4">
                <div className="flex flex-row items-center gap-2 text-[16px] font-bold font-poppins text-[#454545]">
                  <img
                    className="w-6 h-[27px]"
                    src={Calendar}
                    alt="Calendar Icon"
                  />
                  <span>{t("appointments")}</span>
                </div>
                
                {selectedDateAppointments.length > 0 ? (
                  <div className="space-y-4 mt-3">
                    {selectedDateAppointments.map((appointment, index) => (
                      <div key={`${appointment.requestId}-${index}`} className="relative group">
                        <ClientCard
                          client={{
                            name: `${appointment.userDetails.name} ${appointment.userDetails.surname || ''}`,
                            sessionType: t("therapy"),
                            notes: appointment.description || "",
                          }}
                          time={appointment.time}
                          status={appointment.isRefunded ? t("refunded") : t(appointment.status)}
                          statusColor={getStatusColor(appointment.status, appointment.isRefunded).bg}
                          textColor="white"
                        />
                        
                        {/* Refund durumu göster */}
                        {appointment.isRefunded && (
                          <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 text-purple-600" />
                            <span className="text-xs font-semibold text-purple-800">
                              {t("appointmentRefunded")}
                            </span>
                          </div>
                        )}
                        
                        {/* Randevu işlem kontrolleri - Refund edilmişse gösterme */}
                        {!appointment.isRefunded && appointment.status === 'pending' && (
                          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={() => handleUpdateAppointmentStatus(appointment.requestId, 'confirmed')}
                              className="p-1 bg-green-100 rounded-full hover:bg-green-200"
                              title={t("confirm")}
                            >
                              <Check size={16} className="text-green-600" />
                            </button>
                            <button 
                              onClick={() => handleUpdateAppointmentStatus(appointment.requestId, 'rejected')}
                              className="p-1 bg-red-100 rounded-full hover:bg-red-200"
                              title={t("reject")}
                            >
                              <X size={16} className="text-red-600" />
                            </button>
                          </div>
                        )}
                        
                        {!appointment.isRefunded && appointment.status === 'confirmed' && (
                          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={() => handleUpdateAppointmentStatus(appointment.requestId, 'cancelled')}
                              className="p-1 bg-gray-100 rounded-full hover:bg-gray-200"
                              title={t("cancel")}
                            >
                              <X size={16} className="text-gray-600" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center font-poppins mt-3">
                    {t("noAppointmentsForThisDay")}
                  </p>
                )}
              </div>
              
              {/* Müsait Saatler */}
              <div>
                <div className="flex justify-between items-center">
                  <div className="flex flex-row items-center gap-2 text-[16px] font-bold font-poppins text-[#454545]">
                    <Clock className="w-6 h-6" />
                    <span>{t("availableSlotsTitle")}</span>
                  </div>
                  <button 
                    className="p-1 bg-primary rounded-full text-white hover:bg-primary-dark"
                    onClick={() => handleAddAvailability(selectedDate, currentMonth, currentYear)}
                    title={t("addNewAvailability")}
                  >
                    <Plus size={18} />
                  </button>
                </div>
                
                {availabilityLoading ? (
                  <div className="flex justify-center my-4">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="mt-3 space-y-4">
                    {/* Dolu Slotlar */}
                    {bookedSlots.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-orange-600 mb-1.5 uppercase tracking-wide">
                          Booked
                        </p>
                        <div className="space-y-2">
                          {bookedSlots.map((slot, index) => (
                            <div key={index} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                              <div className="flex justify-between items-center">
                                <div className="font-medium font-poppins text-orange-800">
                                  {slot.formatted.time}
                                </div>
                                <span className="text-xs bg-orange-200 text-orange-700 px-2 py-0.5 rounded-full">
                                  Booked
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Müsait Slotlar */}
                    {freeSlots.length > 0 ? (
                      <div>
                        {bookedSlots.length > 0 && (
                          <p className="text-xs font-semibold text-green-600 mb-1.5 uppercase tracking-wide">
                            Available
                          </p>
                        )}
                        <div className="space-y-2">
                          {freeSlots.map((slot, index) => (
                            <div key={index} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex justify-between items-center">
                                <div className="font-medium font-poppins text-green-800">
                                  {slot.formatted.time}
                                </div>
                              </div>
                              <div className="mt-1 flex justify-end">
                                <button
                                  className="text-xs text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteAvailability(slot)}
                                >
                                  {t("remove")}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : bookedSlots.length === 0 ? (
                      <p className="text-gray-500 text-center font-poppins">
                        {t("noAvailableSlotsForThisDay")}
                      </p>
                    ) : null}
                  </div>
                )}
                
                {availabilityError && (
                  <div className="mt-2 p-3 bg-red-50 rounded-lg text-red-800 text-sm">
                    <p>{availabilityError}</p>
                  </div>
                )}
              </div>
              
              {/* İstatistikler */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700">{t("thisMonthStatistics")}</h3>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="p-2 bg-blue-50 rounded border border-blue-100">
                    <div className="text-2xl font-semibold text-blue-700">
                      {flattenedAppointments.length}
                    </div>
                    <div className="text-xs text-blue-600">{t("totalAppointments")}</div>
                  </div>
                  <div className="p-2 bg-green-50 rounded border border-green-100">
                    <div className="text-2xl font-semibold text-green-700">
                      {flattenedAppointments.filter((a) => isConfirmedStatus(a.status)).length}
                    </div>
                    <div className="text-xs text-green-600">{t("confirmedAppointments")}</div>
                  </div>
                  <div className="p-2 bg-yellow-50 rounded border border-yellow-100">
                    <div className="text-2xl font-semibold text-yellow-700">
                      {completedAppointments.length}
                    </div>
                    <div className="text-xs text-yellow-600">{t("completedAppointments")}</div>
                  </div>
                  <div className="p-2 bg-purple-50 rounded border border-purple-100">
                    <div className="text-2xl font-semibold text-purple-700">
                      {availabilitySlots.filter(s => s.startTime > today).length}
                    </div>
                    <div className="text-xs text-purple-600">{t("availableSlots")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

         {/* Takvim Alanı */}
        <div className="w-full md:flex-1 md:max-w-[725px] bg-white rounded-2xl p-4 md:p-6 shadow-xl order-2 lg:order-1">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-2xl md:text-[36px] font-poppins font-bold">
              {`${t(
                `${new Date(currentYear, currentMonth).toLocaleString(
                  "en-US",
                  { month: "long" }
                )}`
              )} ${currentYear}`}
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={handlePrevMonth}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={handleNextMonth}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
          
          {/* Lejant */}
          <div className="flex flex-wrap gap-2 mb-4 text-xs">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-100 rounded-full mr-1"></div>
              <span>{t("today")}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-orange-100 rounded-full mr-1"></div>
              <span>{t("pendingAppointments")}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-100 rounded-full mr-1"></div>
              <span>{t("confirmedAppointments")}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-100 rounded-full mr-1"></div>
              <span>{t("mixedStatusAppointments")}</span>
            </div>
            {/* Müsait zamanlar için yeni lejant eklendi */}
            <div className="flex items-center">
              <div className="w-3 h-3 bg-emerald-50 rounded-full mr-1"></div>
              <span>{t("availableSlots")}</span>
            </div>
          </div>
          
          <div className="text-xs text-gray-500 mb-2 italic">
            {t("rightClickToAddAvailability")}
          </div>

          <div className="h-[500px] md:h-[600px] flex flex-col">
            <TherapistCalendarGrid
              currentYear={currentYear}
              currentMonth={currentMonth}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              appointments={appointments}
              setAppointments={setAppointments}
              handleAddAvailability={handleAddAvailability}
              handleDateClick={handleDateClick}
              availabilitySlots={availabilitySlots} 
            />
          </div>
        </div>
        </div>
      </div>
      
      {/* Modal */}
      {availabilityModalOpen && selectedDateForAvailability && (
        <>       
          <AvailabilityModal
            isOpen={availabilityModalOpen}
            onClose={() => setAvailabilityModalOpen(false)}
            date={selectedDateForAvailability}
            preSelectedSlots={availabilitySlots
              .filter((item) =>
                item.day === selectedDateForAvailability.day &&
                item.month === selectedDateForAvailability.month &&
                item.year === selectedDateForAvailability.year
              )
              .map((item) => ({
                startHour: item.startTime.getHours(),
                endHour: item.endTime.getHours()
              }))
            }
            onSave={(startTime, endTime, price) => {
              console.log("Yeni müsait zaman dilimi eklendi:", {
                date: selectedDateForAvailability,
                startTime,
                endTime,
                price
              });
              setDataFetched(false);
              setAvailabilityModalOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
}

export default TherapistTherapyPlanner;
